import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { Location } from '../../../common/react/Location';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ContentFilterKeyName, ContentFiltersKeys, ContentTypeFilterKey } from '../../../ts/models/ContentFilters';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '../../actions/spaces/filters/FilterActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { updateBreadcrumbHref } from '../../actions/UpdateBreadcrumbHref';
import { populateContentBreadcrumbs } from '../../content/breadcrumbs/BreadcrumbHelpers';
import { updateOrderQueryParamsIfMismatched } from '../../sagas/spaces/content/SortContentSagas';
import { ServiceLocator } from '../../ServiceLocator';
import { SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { getContentTypesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { ConnectedQueryParamUpdateProvider } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteSpace, SiteSpaceProps } from './SiteSpace';

export interface SiteSpaceProviderComponentOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly siteSpaceContentType: SiteSpaceContentType;
  readonly urls: SpacesSiteUrls;
  readonly routeLocationSearch: string;
  readonly renderSpace: (props: SiteSpaceProps) => JSX.Element;
}

export interface SiteSpaceAngularProps {
  readonly telemetry: Telemetry;
  readonly browserTitleService: BrowserTitleService;
}

export interface SiteSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
  readonly updateBreadcrumbHref: (href: string) => void;
}

export type SiteSpaceProviderComponentProps = SiteSpaceProviderComponentOwnProps & SiteSpaceAngularProps & SiteSpaceProviderDispatchProps;

export class SiteSpaceProviderComponent extends React.PureComponent<SiteSpaceProviderComponentProps> {

  public componentDidMount() {
    populateContentBreadcrumbs([], this.props.urls);
    this.props.requestSpaceContents();
    this.props.browserTitleService.setTitleToServerName();

    // populateContentBreadcrumbs sets the 1st breadcrumb to /explore, in case you navigate directly to a page
    // without going through the site space (e.g., navigate directly to a workbook space). We want to override
    // that breadcrumb with the current url if you do visit the Site Space so that we store the correct breadcrumb.
    this.props.updateBreadcrumbHref(Location.instance.href());
  }

  public componentDidUpdate(prevProps: SiteSpaceProviderComponentProps) {
    if ((!_.isUndefined(this.props.siteName) && this.props.siteName !== prevProps.siteName) ||
        this.props.siteSpaceContentType !== prevProps.siteSpaceContentType) {

      const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
      const previousQueryValues = queryString.parse(prevProps.routeLocationSearch);

      // make sure the filters get updated from the url. TFSID 809212
      for (const filterType of _.keys(ContentFiltersKeys)) {
        const value = currentQueryValues[filterType];
        this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value? value + '' : undefined);
      }

      const spaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'site',
        subspaceName: this.props.siteSpaceContentType
      };
      this.props.enterSpace(spaceInfo);

      updateOrderQueryParamsIfMismatched([], spaceInfo);

      // If we just switched tabs and search filters are the same, we know content counts are up to date.
      // They will get set to false by the above EnterSpaceAction.
      if(previousQueryValues[ContentFiltersKeys.search] === currentQueryValues[ContentFiltersKeys.search]) {
        this.props.contentCountsUpToDate();
      }

      this.props.updateBreadcrumbHref(Location.instance.href());

      this.props.requestSpaceContents();
    }
  }

  public render() {
    return this.props.renderSpace({
      angularContext: this.props.angularContext,
      urls: this.props.urls,
      siteSpaceContentType: this.props.siteSpaceContentType,
      telemetry: this.props.telemetry
    });
  }
}

const SiteSpaceAngularHOC = angularProviderHOC<SiteSpaceProviderComponentOwnProps & SiteSpaceProviderDispatchProps, SiteSpaceAngularProps, SiteSpaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
    browserTitleService: ServiceLocator.get<BrowserTitleService>('BrowserTitleService')
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

const AngularWrappedSiteSpace: React.ComponentType<SiteSpaceProviderComponentOwnProps & SiteSpaceProviderDispatchProps>
  = SiteSpaceAngularHOC(SiteSpaceProviderComponent);

const mapDispatchToProps: MapDispatchToProps<SiteSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  setContentFilterNoRequestContent,
  updateBreadcrumbHref
}, dispatch);

export const ConnectedSiteSpaceProvider: React.ComponentClass<SiteSpaceProviderComponentOwnProps>
  = connect(undefined, mapDispatchToProps)(AngularWrappedSiteSpace);

const renderSpace = (props: SiteSpaceProps): JSX.Element => <SiteSpace {...props} />;

export interface SiteSpaceProviderProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly siteSpaceContentType: SiteSpaceContentType;
  readonly urls: SpacesSiteUrls;
}

export const SiteSpaceProvider: React.FunctionComponent<SiteSpaceProviderProps> = React.memo((props) => {

  let subspaceName = props.siteSpaceContentType;
  if (subspaceName === 'datasource') {
    const currentQueryValues = queryString.parse(props.routeLocationSearch);
    const contentTypesParam = currentQueryValues[ContentTypeFilterKey];
    const contentTypes = getContentTypesFromQueryParam(contentTypesParam, []);
    if (contentTypes.length === 1 && contentTypes[0] === 'dataconnection') {
      subspaceName = 'dataconnection';
    }
  }

  const SiteSpaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'site',
    subspaceName
  };
  const siteSpaceProps = {
    siteName: props.siteName,
    urls: props.urls,
    angularContext: props.angularContext,
    siteSpaceContentType: subspaceName,
    routeLocationSearch: props.routeLocationSearch,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={SiteSpaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={true}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedSiteSpaceProvider {...siteSpaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
});
